import { UIElement } from '../ui-element';
import type { ElementConfig } from '../ui-element.type';
import { BrandColors } from '../../global/helpers-marketing';
import { createElement } from '../../global/render-api';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import {
    attributeColorValidator,
    attributeLayoutValidator,
} from './ui-infobox.helpers';
import styles from './ui-infobox.css';
import type { UISurfaceColor } from '../../types/color.type';
import { CMS_CONTENT_MODES } from '../../cms';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIInfobox
 * @element ui-infobox
 * @classdesc Represents a class for <code>ui-infobox</code> element.
 * @property {UIBrandColor} [color="pineapple"] {@attr color} Color of infobox's title.
 * @property {string} [icon] {@attr icon} Glyph of icon, same names as used in ui-icon.
 * @property {string} [label] {@attr label} Title for upper part of infobox.
 * @property {("default" | "bubble")} [layout] {@attr layout} Layout of infobox.
 *  {@desc default}
 *  {@desc bubble}
 * @property {UISurfaceColor} [background] {@attr background} Sets background surface color.
 *  {@desc neutral: Neutral color}
 *  {@desc primary: Primary color}
 *  {@desc secondary: Secondary color}
 *  {@desc tertiary: Tertiary color - Default}
 *  {@desc outlined: Outlined neutral color}
 * @property {string} [headingAriaLabel] {@attr heading-aria-label} Sets the aria-label on the infobox header element.
 * @property {HTMLElement} header Read-only shortcut to header.
 * @slot
 * @example
 * <ui-infobox color="turquoise" icon="transport" layout="bubble">
 *   <p>I am turquoise car.</p>
 * </ui-infobox>
 */
@Component({
    tag: 'ui-infobox',
    styles,
    cms: {
        mode: CMS_CONTENT_MODES.Children,
    },
})
export class UIInfobox extends UIElement {
    @Attribute({
        type: String,
        validate: attributeColorValidator,
    })
    color: string = BrandColors.Pineapple;

    @Attribute(String) background?: UISurfaceColor;
    @Attribute(String) headingAriaLabel?: string;
    @Attribute(String) icon?: string;
    @Attribute(String) label?: string;
    @Attribute({
        type: String,
        validate: attributeLayoutValidator,
    })
    layout?: string;

    @Child('.ui-infobox__heading') header?:
        | HTMLDivElement
        | HTMLHeadingElement
        | null;

    /**
     * @type {string[]}
     */
    static get observedAttributes(): Array<string> {
        return ['label', 'heading-aria-label'];
    }

    /**
     * @private
     * @returns {ElementConfig}
     */
    buildHeader(): ElementConfig {
        const headerChildren: Array<ElementConfig | string> = [];
        if (this.icon) {
            headerChildren.push({
                tagName: 'ui-icon',
                attributes: {
                    glyph: this.icon,
                },
            });
        }
        headerChildren.push(this.label ?? '');
        return {
            tagName: !this.label ? 'div' : 'h3',
            attributes: {
                'aria-hidden': !this.label ? 'true' : null,
                'aria-label': this.headingAriaLabel ?? null,
            },
            classList: {
                'ui-infobox__heading': true,
            },
            children: headerChildren,
        };
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: string | null,
        newValue: string | null
    ): void {
        if (oldValue === newValue) {
            return;
        }
        switch (name) {
            case 'label':
            case 'heading-aria-label':
                if (!this.header) {
                    return;
                }
                const newHeader = createElement(this.buildHeader());
                newHeader !== null && this.replaceChild(newHeader, this.header);
                break;
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.insertElements([
            this.buildHeader(),
            {
                tagName: 'article',
                children: this.detachChildNodes(),
            },
        ]);
    }
}
